/**
 * Lead inventory table rows and lead-detail workspace.
 */
import { formatInteger } from "./format.js";
import { detailMetric, escapeAttr, escapeHtml, labelize, pill } from "./dashboard-html.js";
import {
  leadDetailCurrentBalanceHtml,
  leadDetailCurrentLoanValueHtml,
  leadDetailEvidencePathHtml,
  leadDetailForsythTaxHtml,
  leadDetailNoticeHtml,
  leadDetailOriginalDeedHtml,
  leadDetailPresentLoanHtml,
  leadDetailSection,
  sourceDocumentDetailHtml
} from "./dashboard-lead-detail.js";
import { downstreamAcquisitionFormsHtml } from "./dashboard-acquisition-forms.js";
import { acquisitionOfferFormHtml, acquisitionPipelineFormHtml, contactOutreachFormHtml } from "./dashboard-contact-prep.js";
import {
  leadGenerationOverflowHtml,
  leadRowPrimaryActionsHtml,
  mappedLeadGenerationNextAction,
  outOfStageLeadActionsHtml
} from "./dashboard-operator-view.js";
import {
  contactDetailHtml,
  eventDetailHtml,
  factDetailHtml,
  outreachDetailHtml,
  ownerDetailHtml,
  pipelineDetailHtml,
  segmentDetailHtml,
  taskDetailHtml
} from "./dashboard-detail-cards.js";
import type { LeadFactoryLead, LeadFactoryLeadDetailResponse } from "./dashboard-lead-factory-types.js";

export function leadRowHtml(lead: LeadFactoryLead): string {
  const title = lead.property_address || lead.parcel_id || lead.id;
  const next = mappedLeadGenerationNextAction(lead);
  return `
    <tr data-lead-id="${escapeAttr(lead.id)}">
      <td>
        <strong>${escapeHtml(title)}</strong>
        <small>${escapeHtml(lead.county)} · ${escapeHtml(lead.id)}</small>
      </td>
      <td>${pill(labelize(lead.status), lead.status)}</td>
      <td>${pill(labelize(lead.contact_readiness), lead.contact_readiness)}</td>
      <td>
        <strong>${formatInteger(lead.source_document_count ?? 0)} docs</strong>
        <small>${formatInteger(lead.open_task_count ?? 0)} open tasks</small>
      </td>
      <td>
        <strong>${escapeHtml(lead.owner_name || "Owner unknown")}</strong>
        <small>${escapeHtml(lead.assigned_to ? `Assigned: ${lead.assigned_to}` : "Unassigned")}</small>
      </td>
      <td>
        <div class="row-actions">
          ${leadRowPrimaryActionsHtml(lead.id, next)}
          ${leadGenerationOverflowHtml(lead.id, next?.action)}
        </div>
      </td>
    </tr>
  `;
}

export function leadDetailHtml(detail: LeadFactoryLeadDetailResponse): string {
  const lead = detail.lead;
  const title = lead.property_address || lead.parcel_id || lead.owner_name || lead.id;
  return `
    <div class="detail-inner lead-detail" data-open-lead-id="${escapeAttr(lead.id)}">
      <div class="detail-header">
        <div>
          <h2>${escapeHtml(title)}</h2>
          <p class="subtle">${escapeHtml(lead.county)} · ${escapeHtml(lead.id)}</p>
        </div>
        <div class="status-stack">
          ${pill(labelize(lead.status), lead.status)}
          ${pill(labelize(lead.contact_readiness), lead.contact_readiness)}
          ${pill(labelize(lead.identity_state), lead.identity_state)}
        </div>
      </div>

      ${leadDetailEvidencePathHtml(detail)}
      ${leadDetailNoticeHtml(detail)}
      ${leadDetailCurrentBalanceHtml(detail)}
      ${leadDetailCurrentLoanValueHtml(detail)}
      ${leadDetailOriginalDeedHtml(detail)}
      ${leadDetailForsythTaxHtml(detail)}
      ${leadDetailPresentLoanHtml(detail)}

      <div class="detail-metrics">
        ${detailMetric("Source docs", formatInteger(detail.documents.length))}
        ${detailMetric("Open tasks", formatInteger(detail.tasks.filter((task) => ["open", "assigned", "blocked"].includes(task.status)).length))}
        ${detailMetric("Facts", formatInteger(detail.facts.length))}
        ${detailMetric("Events", formatInteger(detail.events.length))}
      </div>

      <section>
        <h3>Lead</h3>
        <div class="detail-grid">
          ${detailMetric("Owner", lead.owner_name || "Unknown")}
          ${detailMetric("Property", lead.property_address || "Unknown")}
          ${detailMetric("Mortgage", lead.mortgage_amount ? `$${formatInteger(lead.mortgage_amount)}` : "Unknown")}
          ${detailMetric("Assigned", lead.assigned_to || "Unassigned")}
        </div>
      </section>

      ${leadDetailSection("Source Documents", detail.documents.map(sourceDocumentDetailHtml), "No source documents are linked.")}
      ${leadDetailSection("Segments", detail.segments.map(segmentDetailHtml), "No active segment assignments.")}
      ${leadDetailSection("Tasks", detail.tasks.slice(0, 12).map(taskDetailHtml), "No tasks for this lead.")}
      ${leadDetailSection("Owners And Contacts", [...detail.owners.map(ownerDetailHtml), ...detail.contacts.map(contactDetailHtml)], "No owner/contact records.")}
      ${leadDetailSection("Facts", detail.facts.slice(0, 12).map((fact) => factDetailHtml(fact)), "No extracted facts.")}
      ${leadDetailSection("Outreach", detail.outreach.slice(0, 8).map(outreachDetailHtml), "No outreach attempts.")}
      ${leadDetailSection("Pipeline", detail.pipeline.slice(0, 8).map(pipelineDetailHtml), "No acquisition pipeline entries.")}
      ${leadDetailSection("Workflow Events", detail.events.slice(0, 12).map(eventDetailHtml), "No workflow history.")}

      <section data-lead-detail-next-action>
        <h3>Next action</h3>
        ${contactOutreachFormHtml(lead)}
      </section>

      <details class="advanced-dashboard-section" data-lead-detail-advanced>
        <summary>
          <span>
            <strong>Advanced</strong>
            <small>Acquisition, finance, merge/split — not the lead-generation path</small>
          </span>
        </summary>
        ${outOfStageLeadActionsHtml(lead.id)}
        ${acquisitionPipelineFormHtml(lead)}
        ${acquisitionOfferFormHtml(lead)}
        ${downstreamAcquisitionFormsHtml(lead)}
      </details>
    </div>
  `;
}

